/**
 * Combat resolution (rule.md §7). Three entry points share one core:
 *  - `applyAttack` — atomic god-view (sim / replay): both elements known.
 *  - `applyDeclareAttack` — live step 1: records the attacker's committed pick.
 *  - `applyResolveAttack` — live step 2: the defender's pick resolves the clash.
 *
 * The core (`resolveCombat`) does:
 *  1. both sides spend their chosen element (cooldown, shared atk+def),
 *  2. the wheel decides win / lose / standoff,
 *  3. the loser takes 1 HP (unless a Federation negates it with Bastion),
 *  4. a unit at 0 HP is removed; on a kill the attacker may advance (§7.5).
 */
import type { CellId } from "../data/board";
import type {
  AttackAction,
  DeclareAttackAction,
  ResolveAttackAction,
} from "../types/action";
import type { PlayerId } from "../types/common";
import type { Element } from "../types/element";
import type {
  AugmentedWarState,
  CombatOutcome,
  CombatRecord,
  Unit,
} from "../types/game-state";
import { elementOutcome } from "../utils/element";
import { opponentOf } from "../utils/state";
import { finalize, spendAction, updatePlayer } from "./shared";

interface CombatParams {
  readonly from: CellId;
  readonly target: CellId;
  readonly attackerId: PlayerId;
  readonly attackerElement: Element;
  readonly defenderElement: Element;
  readonly attackerUseBastion?: boolean;
  readonly defenderUseBastion?: boolean;
  readonly advance?: boolean;
}

/** Steps 1–4 above. Assumes validated input (attacker + adjacent defender present). */
function resolveCombat(
  state: AugmentedWarState,
  p: CombatParams,
): Pick<AugmentedWarState, "board" | "players" | "lastCombat"> {
  const { from, target, attackerId, attackerElement, defenderElement } = p;
  const attacker = state.board[from];
  const defender = state.board[target];
  const defenderId = opponentOf(attackerId);

  // 1. spend elements for both sides (cooldown, rule.md §7.3)
  let players = updatePlayer(state.players, attackerId, {
    spentElements: [
      ...state.players[attackerId].spentElements,
      attackerElement,
    ],
  });
  players = updatePlayer(players, defenderId, {
    spentElements: [...players[defenderId].spentElements, defenderElement],
  });

  // 2. resolve the wheel from the attacker's perspective
  const wheel = elementOutcome(attackerElement, defenderElement);
  const outcome: CombatOutcome =
    wheel === "draw"
      ? "standoff"
      : wheel === "win"
        ? "attackerWins"
        : "defenderWins";

  const board: Record<string, Unit> = { ...state.board };
  let damagedCell: CellId | undefined;
  let removedCell: CellId | undefined;
  let advancedTo: CellId | undefined;
  let bastionNegated: PlayerId | undefined;

  // 3. apply 1 HP of damage to the loser (none on a standoff)
  if (wheel !== "draw" && attacker && defender) {
    const loserCell = wheel === "win" ? target : from;
    const loserId = wheel === "win" ? defenderId : attackerId;
    const loserUnit = wheel === "win" ? defender : attacker;
    const useBastion =
      (loserId === attackerId ? p.attackerUseBastion : p.defenderUseBastion) ??
      false;
    const canBastion =
      players[loserId].faction === "federation" &&
      players[loserId].bastionAvailable &&
      useBastion;

    if (canBastion) {
      bastionNegated = loserId;
      players = updatePlayer(players, loserId, { bastionAvailable: false });
    } else {
      const newHp = loserUnit.hp - 1;
      if (newHp <= 0) {
        delete board[loserCell];
        removedCell = loserCell;
      } else {
        board[loserCell] = { ...loserUnit, hp: newHp };
        damagedCell = loserCell;
      }
    }
  }

  // 4. advance-on-kill: attacker may occupy the vacated cell (rule.md §7.5)
  if (
    outcome === "attackerWins" &&
    removedCell === target &&
    attacker &&
    (p.advance ?? false)
  ) {
    delete board[from];
    board[target] = attacker; // attacker keeps its HP
    advancedTo = target;
  }

  const lastCombat: CombatRecord = {
    attacker: from,
    target,
    attackerElement,
    defenderElement,
    outcome,
    ...(damagedCell ? { damagedCell } : {}),
    ...(removedCell ? { removedCell } : {}),
    ...(bastionNegated ? { bastionNegated } : {}),
    ...(advancedTo ? { advancedTo } : {}),
  };

  return { board, players, lastCombat };
}

/** Atomic god-view combat (sim / replay): resolve immediately, spend 1 AP. */
export function applyAttack(
  state: AugmentedWarState,
  action: AttackAction,
): AugmentedWarState {
  const { from, target, playerId } = action;
  if (!state.board[from] || !state.board[target]) return state; // unreachable post-validation
  return finalize({
    ...state,
    ...resolveCombat(state, {
      from,
      target,
      attackerId: playerId,
      attackerElement: action.attackerElement,
      defenderElement: action.defenderElement,
      ...(action.attackerUseBastion !== undefined
        ? { attackerUseBastion: action.attackerUseBastion }
        : {}),
      ...(action.defenderUseBastion !== undefined
        ? { defenderUseBastion: action.defenderUseBastion }
        : {}),
      ...(action.advance !== undefined ? { advance: action.advance } : {}),
    }),
    ...spendAction(state, "attack"),
  });
}

/**
 * Live step 1: record the attacker's committed element + target and spend the
 * attack's AP now (the declaration is the action). No combat happens yet — the
 * defender must ResolveAttack next; every other action is blocked meanwhile.
 */
export function applyDeclareAttack(
  state: AugmentedWarState,
  action: DeclareAttackAction,
): AugmentedWarState {
  const { playerId, from, target, attackerElement } = action;
  return {
    ...state,
    ...spendAction(state, "attack"),
    pendingAttack: {
      attacker: playerId,
      from,
      target,
      attackerElement,
      ...(action.attackerUseBastion !== undefined
        ? { attackerUseBastion: action.attackerUseBastion }
        : {}),
      ...(action.advance !== undefined ? { advance: action.advance } : {}),
    },
  };
}

/** Live step 2: the defender's element resolves the pending clash (AP already spent). */
export function applyResolveAttack(
  state: AugmentedWarState,
  action: ResolveAttackAction,
): AugmentedWarState {
  const pending = state.pendingAttack;
  if (!pending) return state; // unreachable post-validation
  const { pendingAttack: _resolved, ...rest } = state;
  return finalize({
    ...rest,
    ...resolveCombat(state, {
      from: pending.from,
      target: pending.target,
      attackerId: pending.attacker,
      attackerElement: pending.attackerElement,
      defenderElement: action.defenderElement,
      ...(pending.attackerUseBastion !== undefined
        ? { attackerUseBastion: pending.attackerUseBastion }
        : {}),
      ...(action.defenderUseBastion !== undefined
        ? { defenderUseBastion: action.defenderUseBastion }
        : {}),
      ...(pending.advance !== undefined ? { advance: pending.advance } : {}),
    }),
  });
}
